'use client';

import { useTranslations } from 'next-intl';
import { AlertCircle, Package, Trash2, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link, useRouter } from '@/i18n/routing';
import type { Shipment } from '../types';

type ReceptionStatus = 'matched' | 'processing' | 'not_matched' | 'pending';

const RECEPTION_STATUS_STYLES: Record<
  ReceptionStatus,
  { dot: string; badge: string }
> = {
  matched: {
    dot: 'bg-green-500',
    badge:
      'bg-green-50 text-green-700 hover:bg-green-50 dark:bg-green-950 dark:text-green-300'
  },
  processing: {
    dot: 'bg-amber-500',
    badge:
      'bg-amber-50 text-amber-700 hover:bg-amber-50 dark:bg-amber-950 dark:text-amber-300'
  },
  not_matched: {
    dot: 'bg-red-500',
    badge:
      'bg-red-50 text-red-700 hover:bg-red-50 dark:bg-red-950 dark:text-red-300'
  },
  pending: {
    dot: 'bg-gray-400',
    badge:
      'bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
  }
};

function getReceptionStatus(shipment: Shipment): ReceptionStatus {
  if (shipment.active_session) return 'processing';
  if (shipment.latest_session?.status === 'completed') {
    return shipment.latest_session.count_status === 'mismatched'
      ? 'not_matched'
      : 'matched';
  }
  return 'pending';
}

interface ReceptionCardProps {
  shipment: Shipment;
  onDelete: (shipment: Shipment) => void;
}

export function ReceptionCard({ shipment, onDelete }: ReceptionCardProps) {
  const t = useTranslations('reception');
  const router = useRouter();

  const status = getReceptionStatus(shipment);
  const styles = RECEPTION_STATUS_STYLES[status];
  const isUrgent = shipment.priority === 'urgent';
  const isInProgress = !!shipment.active_session;

  const receptionStatusLabels: Record<ReceptionStatus, string> = {
    matched: t('metricMatched'),
    processing: t('metricProcessing'),
    not_matched: t('metricNotMatched'),
    pending: t('statusPending')
  };

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: shipment_number + status */}
        <div className='mb-4 flex items-start justify-between'>
          <div className='flex items-center gap-2'>
            {isUrgent && (
              <AlertCircle className='h-4 w-4 shrink-0 animate-pulse text-red-500' />
            )}
            <Link
              href={`/dashboard/shipment-statement/${shipment.id}`}
              className={cn(
                'font-mono font-bold hover:underline',
                isUrgent ? 'text-red-600 dark:text-red-400' : 'text-primary'
              )}
            >
              {shipment.shipment_number}
            </Link>
          </div>
          <div className='flex items-center gap-1.5'>
            <Badge variant='secondary' className={cn('gap-1.5', styles.badge)}>
              <span className={cn('h-2 w-2 rounded-full', styles.dot)} />
              {receptionStatusLabels[status]}
            </Badge>
          </div>
        </div>

        {/* Middle: details */}
        <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
          <p>
            <span className='font-medium'>{t('judicialEntity')}:</span>{' '}
            {shipment.judicial_entity}
          </p>
          <div className='flex items-center gap-4'>
            <span className='flex items-center gap-1'>
              <Package className='h-3.5 w-3.5' />
              {t('boxes', { count: shipment.box_count })}
            </span>
            {shipment.reception_sessions_count != null && (
              <span className='flex items-center gap-1'>
                <Users className='h-3.5 w-3.5' />
                {t('sessionsCount')}: {shipment.reception_sessions_count}
              </span>
            )}
          </div>
          {shipment.driver && (
            <p>
              <span className='font-medium'>{t('driver')}:</span>{' '}
              {shipment.driver.name}
            </p>
          )}
        </div>

        {/* Bottom: action buttons */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          {shipment.active_session ? (
            <>
              <Button
                size='sm'
                onClick={() =>
                  router.push(
                    `/dashboard/shipment-statement/${shipment.id}/continue`
                  )
                }
              >
                {t('followUp')}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  router.push(`/dashboard/shipment-statement/${shipment.id}`)
                }
              >
                {t('view')}
              </Button>
            </>
          ) : shipment.latest_session?.status === 'completed' ? (
            <>
              <Badge className='bg-green-500 text-white'>
                {t('completed')}
              </Badge>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  router.push(`/dashboard/shipment-statement/${shipment.id}`)
                }
              >
                {t('view')}
              </Button>
            </>
          ) : (
            <Button
              size='sm'
              onClick={() =>
                router.push(
                  `/dashboard/shipment-statement/${shipment.id}/continue`
                )
              }
            >
              {t('initialize')}
            </Button>
          )}
          <Button
            variant='ghost'
            size='sm'
            disabled={isInProgress}
            onClick={() => onDelete(shipment)}
            className='text-muted-foreground hover:text-destructive'
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
